import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { DropdownOption, GlobalDropdownField } from '../../components/GlobalComponents/GlobalDropdownField';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalTextArea } from '../../components/GlobalComponents/GlobalTextArea';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';

interface EditingCourse {
  course_id: string;
  name: string;
  title: string;
  course_key?: string | null;
  description: string | null;
  status: string;
}

interface AddCourseModalProps {
  visible: boolean;
  instituteId: string;
  onHide: () => void;
  onSuccess?: () => void;
  editingCourse?: EditingCourse | null;
}

const AddCourseModal: React.FC<AddCourseModalProps> = ({
  visible,
  instituteId,
  onHide,
  onSuccess,
  editingCourse = null
}) => {
  const isEditing = !!editingCourse;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    title: '',
    course_key: '',
    description: '',
    status: 'active'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (visible) {
      if (editingCourse) {
        setFormData({
          name: editingCourse.name,
          title: editingCourse.title,
          course_key: editingCourse.course_key ?? '',
          description: editingCourse.description ?? '',
          status: editingCourse.status
        });
      } else {
        setFormData({ name: '', title: '', course_key: '', description: '', status: 'active' });
      }
      setErrors({});
      setError(null);
    }
  }, [visible, editingCourse]);

  const statusOptions: DropdownOption[] = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Course name is required';
    if (!formData.title.trim()) newErrors.title = 'Course title is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setError('Please fill all required fields correctly');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditing) {
        const payload: Record<string, any> = {
          name: formData.name.trim(),
          title: formData.title.trim(),
          course_key: formData.course_key.trim() || null,
          description: formData.description.trim() || null,
          status: formData.status
        };

        const response = await apiClient.put(`/v1/courses/${editingCourse!.course_id}`, payload);

        if (response.data.success) {
          handleClose();
          onSuccess?.();
        } else {
          setError(response.data.message || 'Failed to update course');
        }
      } else {
        const payload = {
          institute_id: instituteId,
          name: formData.name.trim(),
          title: formData.title.trim(),
          course_key: formData.course_key.trim() || null,
          description: formData.description.trim() || null,
          status: formData.status
        };

        const response = await apiClient.post('/v1/courses', payload);

        if (response.data.success) {
          handleClose();
          onSuccess?.();
        } else {
          setError(response.data.message || 'Failed to create course');
        }
      }
    } catch (err: any) {
      console.error(`Failed to ${isEditing ? 'update' : 'create'} course:`, err);
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        `Failed to ${isEditing ? 'update' : 'create'} course`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', title: '', course_key: '', description: '', status: 'active' });
    setErrors({});
    setError(null);
    onHide();
  };

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{ width: '90vw', maxWidth: '900px' }}
      dismissableMask
      draggable={false}
      pt={{
        root: {
          style: {
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }
        }
      }}
    >
      <div>
        {/* Modal Header */}
        <div style={{
          background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '150px',
            height: '150px',
            background: 'rgba(212, 241, 126, 0.1)',
            borderRadius: '50%',
            filter: 'blur(40px)'
          }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              display: 'inline-block',
              padding: '4px 10px',
              background: 'rgba(212, 241, 126, 0.2)',
              borderRadius: '16px',
              marginBottom: '8px',
              fontSize: '0.65rem',
              fontWeight: '600',
              letterSpacing: '0.5px',
              textTransform: 'uppercase'
            }}>
              Course Management
            </div>
            <h2 style={{
              margin: '0 0 6px 0',
              fontSize: '1.4rem',
              fontWeight: '700',
              color: 'white'
            }}>
              {isEditing ? 'Edit Course' : 'Add New Course'}
            </h2>
            <p style={{
              margin: '0',
              color: 'rgba(255,255,255,0.8)',
              fontSize: '0.8rem'
            }}>
              {isEditing
                ? `Editing: ${editingCourse!.name}`
                : 'Create a new course for your institute'}
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <Message
            severity="error"
            text={error}
            style={{ marginBottom: '20px', fontSize: '0.9rem', fontWeight: '500' }}
          />
        )}

        {/* Form Content */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px',
          border: '1px solid #f0f0f0'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <GlobalInputField
              label="Course Name"
              required
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g., B.Tech, MBA, M.Sc"
              icon="pi pi-book"
              error={errors.name}
            />

            <GlobalInputField
              label="Course Title"
              required
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="e.g., Bachelor of Technology, Master of Business Administration"
              icon="pi pi-graduation-cap"
              error={errors.title}
            />

            {/* Course Key Field with blue highlight and note */}
            <div style={{
              border: '1.5px solid #3b82f6',
              borderRadius: '10px',
              padding: '14px 16px 10px',
              background: '#eff6ff',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '-11px',
                left: '14px',
                background: '#3b82f6',
                color: 'white',
                fontSize: '0.65rem',
                fontWeight: '700',
                letterSpacing: '0.5px',
                padding: '2px 8px',
                borderRadius: '6px',
                textTransform: 'uppercase'
              }}>
                Used for ID Generation
              </div>

              <GlobalInputField
                label="Course Key"
                value={formData.course_key}
                onChange={(e) => handleInputChange('course_key', e.target.value.toUpperCase())}
                placeholder="e.g., BTECH, MBA, DCA (optional)"
                icon="pi pi-key"
              />

              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '6px',
                marginTop: '8px',
                padding: '8px 10px',
                background: '#dbeafe',
                borderRadius: '7px',
                border: '1px solid #bfdbfe'
              }}>
                <i className="pi pi-info-circle" style={{
                  color: '#1d4ed8',
                  fontSize: '13px',
                  marginTop: '1px',
                  flexShrink: 0
                }} />
                <p style={{
                  margin: 0,
                  fontSize: '0.75rem',
                  color: '#1e40af',
                  lineHeight: '1.5'
                }}>
                  This key is used as initials in auto-generated IDs such as{' '}
                  <strong style={{ fontWeight: 600 }}>Enrollment No.</strong>{' '}
                  (e.g., <code style={{
                    background: '#bfdbfe',
                    padding: '1px 5px',
                    borderRadius: '4px',
                    fontSize: '0.72rem',
                    fontFamily: 'monospace'
                  }}>
                    {formData.course_key.trim() || 'BTECH'}-ENR-2026-1
                  </code>).
                  If left blank, initials will be derived from the course title automatically.
                </p>
              </div>
            </div>

            <GlobalTextArea
              label="Description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Enter course description (optional)"
              rows={4}
              maxLength={500}
            />

            <GlobalDropdownField
              label="Status"
              required
              value={formData.status}
              onChange={(val) => handleInputChange('status', val)}
              options={statusOptions}
              placeholder="Select status"
              icon="pi pi-check-circle"
            />
          </div>
        </div>

        {/* Modal Actions */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          flexWrap: 'wrap'
        }}>
          <GlobalButton
            label="Cancel"
            variant="outline"
            icon="pi pi-times"
            onClick={handleClose}
            disabled={loading}
          />
          <GlobalButton
            label={loading
              ? (isEditing ? 'Saving...' : 'Creating Course...')
              : (isEditing ? 'Save Changes' : 'Create Course')}
            variant="primary"
            icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
            onClick={handleSubmit}
            disabled={loading}
            loading={loading}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default AddCourseModal;